import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import * as gql from '@/graphql'
import { numFormat } from '@/utils/num_format'
import dayjs from 'dayjs'
import Decimal from 'decimal.js'

interface CardItem {
  label: string
  value: string
  showTip?: boolean
  tipContent?: string
}

export interface SummaryOverviewData {
  HKD: {
    totalAsset: string
    totalBuyingPower: string
    totalCash: string
  }
  USD: {
    totalAsset: string
    totalBuyingPower: string
    totalCash: string
  }
}

export interface CashBreakdownItem {
  label: string
  value: string
}

export interface CashBreakdownData {
  HKD: CashBreakdownItem[]
  USD: CashBreakdownItem[]
}

export interface TableColumn {
  prop: string
  label: string
  minWidth?: string
  fixed?: string
  align?: string
  showTip?: boolean
  tipContent?: string
  type?: 'action' | 'twoLines' | 'tagWithText'
  propList?: { prop: string, style?: 'tag' | 'exchange' }[]
  separator?: string
}

interface UseAccountDetailOptions {
  clientId: Ref<string>
  activeTopTab: Ref<string>
  activeSubTab: Ref<string>
  activeOtherCurrency: Ref<string>
}

export interface SubTabItem {
  label: string
  name: string
}

interface UseAccountDetailReturn {
  loading: Ref<boolean>
  currentCards: ComputedRef<CardItem[]>
  currentColumns: ComputedRef<TableColumn[]>
  currentTableData: ComputedRef<any[]>
  visibleStockSubTabs: ComputedRef<SubTabItem[]>
  summaryOverviewData: ComputedRef<SummaryOverviewData>
  cashBreakdownData: ComputedRef<CashBreakdownData>
  refreshTime: Ref<string>
  refresh: () => void
}

const STOCK_COLUMNS: TableColumn[] = [
  { prop: 'code', label: '代码', minWidth: '120', type: 'tagWithText', propList: [{ prop: 'code' }, { prop: 'exchangeType', style: 'exchange' }] },
  { prop: 'name', label: '名称', minWidth: '140' },
  { prop: 'marketValue', label: '总市值', minWidth: '120' },
  { prop: 'available', label: '可用', minWidth: '110' },
  { prop: 'hold', label: '持有', minWidth: '110' },
  { prop: 'cost', label: '成本', minWidth: '100', showTip: true, tipContent: '「成本价」仅供参考，成本价=（买入前的平均成本价x数量+此次买入的价格数x数量）/买入后持有数量。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「成本价」，请电邮至cs@goldhorse.com.hk。' },
  { prop: 'currentPrice', label: '现价', minWidth: '100' },
  { prop: 'closePrice', label: '昨收', minWidth: '100' },
  { prop: 'profit', label: '盈亏', minWidth: '120', showTip: true, tipContent: '「盈亏」仅供参考，该数据或未能反映整体持仓的实时盈亏。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「盈亏」，请电邮至cs@goldhorse.com.hk。', type: 'twoLines', propList: [{ prop: 'profit' }, { prop: 'profitPercent' }] },
  { prop: '', label: '操作', type: 'action', fixed: 'right', align: 'center', minWidth: '152' }
]

const knownStockMarketTypes = ['HK', 'US', 'SZ', 'SH', 'TW', 'JP', 'SG']
const aShareExchangeTypes = ['SZ', 'SH', 'A']
const cashBaseMoneyTypes = ['HKD', 'USD', 'CNY']

const STOCK_SUB_TABS: SubTabItem[] = [
  { label: '港股', name: 'HK' },
  { label: '美股', name: 'US' },
  { label: 'A股', name: 'A' },
  { label: '台股', name: 'TW' },
  { label: '日股', name: 'JP' },
  { label: '新加坡股', name: 'SG' },
  { label: '其他股票', name: 'other' }
]

const STOCK_TABS_ALWAYS_SHOW = ['HK', 'US', 'A']

const STOCK_HIDE_AVAILABLE_SUB_TABS = ['TW', 'JP', 'SG', 'other']
const STOCK_SHOW_CLOSE_PRICE_SUB_TABS = ['TW', 'JP', 'SG']
const STOCK_SHOW_CURRENT_PRICE_DISPLAY_SUB_TABS = ['HK', 'US', 'A']
const STOCK_SHOW_PROFIT_SUB_TABS = ['HK', 'US', 'A']
const STOCK_SHOW_ACTION_SUB_TABS = ['HK', 'US', 'A']

const FUND_COLUMNS: TableColumn[] = [
  // { prop: 'name', label: '名称', minWidth: '220' },
  { prop: 'name', label: '名称/ISIN', minWidth: '220', type: 'tagWithText', separator: ' / ', propList: [{ prop: 'productName' }, { prop: 'productCode' }] },
  { prop: 'marketValue', label: '总市值', minWidth: '120' },
  { prop: 'currentAmount', label: '数量', minWidth: '120' }
]

const BOND_COLUMNS: TableColumn[] = [
  { prop: 'underlying', label: '名称' },
  { prop: 'stockCode', label: 'ISIN', minWidth: '120' },
  { prop: 'marketValueDisplay', label: '市值' },
  { prop: 'currentAmount', label: '持有份数' },
  { prop: 'maturityDays', label: '剩余天数' },
  { prop: 'yieldToMaturity', label: '到期收益率' },
  { prop: 'maturityDate', label: '到期时间' }
]

const SPS_COLUMNS: TableColumn[] = [
  { prop: 'subType', label: '产品/ISIN', fixed: 'left', minWidth: '260', type: 'tagWithText', propList: [{ prop: 'subType', style: 'tag' }, { prop: 'stockCode' }] },
  { prop: 'investmentAmount', label: '投资金额', minWidth: '150' },
  { prop: 'issuer', label: '发行商', minWidth: '120' },
  { prop: 'underlying', label: '标的', minWidth: '220' },
  { prop: 'couponRatio', label: '年化票息', minWidth: '120' },
  { prop: 'maturityDate', label: '到期时间', minWidth: '124' }
]

const EXTRA_COLUMNS: TableColumn[] = [
  { prop: 'name', label: '名称/ISIN', minWidth: '220', type: 'tagWithText', separator: ' / ', propList: [{ prop: 'stockName' }, { prop: 'stockCode' }] },
  { prop: 'marketValueDisplay', label: '总市值', minWidth: '120' },
  { prop: 'currentAmount', label: '数量', minWidth: '120' }
]

const COLUMNS_MAP: Record<string, TableColumn[]> = {
  stock: STOCK_COLUMNS,
  fund: FUND_COLUMNS,
  bond: BOND_COLUMNS,
  sps: SPS_COLUMNS,
  extra: EXTRA_COLUMNS
}

const MARKET_LABEL_MAP: Record<string, string> = {
  HK: '港股总市值',
  US: '美股总市值',
  A: 'A股总市值',
  TW: '台股总市值',
  JP: '日股总市值',
  SG: '新加坡股总市值',
  other: '其他股票总市值'
}

const EXT_TYPE_MAP: Record<string, string> = {
  fund: 'fund',
  bond: 'bond',
  sps: 'sps',
  extra: 'extra'
}

function formatPercent(val: any): string {
  if (val == null || val === '') return '--'
  const num = Number(val)
  if (isNaN(num)) return '--'
  const percent = num * 100
  const truncated = Math.floor(percent * 100) / 100
  return truncated.toFixed(2) + '%'
}

function resolveExtMoneyType(sub: string): string {
  if (sub === 'OTHER_HKD') return 'OTHER_HKD'
  return sub
}

function isOtherCurrencyExtTab(sub: string): boolean {
  return sub === 'OTHER_HKD'
}

function hasStockPositionInSubTab(sub: string, rows: any[]): boolean {
  if (!rows.length) return false
  if (sub === 'A') {
    return rows.some((item) => aShareExchangeTypes.includes(item.exchangeType))
  }
  if (sub === 'other') {
    return rows.some((item) => !knownStockMarketTypes.includes(item.exchangeType))
  }
  return rows.some((item) => item.exchangeType === sub)
}

export function useAccountDetail(options: UseAccountDetailOptions): UseAccountDetailReturn {
  const { clientId, activeTopTab, activeSubTab, activeOtherCurrency } = options

  const loading = ref(false)
  const refreshTime = ref(dayjs().format('YYYY-MM-DD HH:mm:ss'))

  const positionData = ref<any>({})
  const fundInfoData = ref<any>({})
  const lifecycleData = ref<any>({})
  const forexList = ref<any>([])

  const { refetch, onResult } = useQuery(gql.lpoaPositionFundLifecycle, { filter: {} })

  onResult((res) => {
    const { lpoaPositionList, lpoaFundInfoList, clientLifecycle, lpoaForex } = res?.data || {}

    positionData.value =
      lpoaPositionList?.data?.find(
        (item: any) => item.clientid === clientId.value
      ) || {}

    fundInfoData.value =
      lpoaFundInfoList?.data?.find(
        (item: any) => item.clientid === clientId.value
      ) || {}

    lifecycleData.value =
      clientLifecycle?.data?.find(
        (item: any) => item.clientId === clientId.value
      ) || {}

    forexList.value = lpoaForex?.data || []

    loading.value = false
    refreshTime.value = dayjs().format('YYYY-MM-DD HH:mm:ss')
  })

  // #region 汇率计算
  const getRate = (targetCcy: string): number | null => {
    const code = String(targetCcy || '').toUpperCase()
    if (!code) return null
    const directRate = forexList.value.find((item: any) => item.ccy === code)?.rate
    const rate = Number(directRate)
    return Number.isFinite(rate) && rate > 0 ? rate : null
  }

  // amount: 原金额
  // fromCcy: 原币种（如 USD）
  // toCcy: 目标币种（如 CNY）
  const convertCurrencyAmount = (
    amount: number | string,
    fromCcy: string,
    toCcy: string
  ): number | null => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return null

    const from = String(fromCcy || '').toUpperCase()
    const to = String(toCcy || '').toUpperCase()
    if (!from || !to || from === to) return value

    const fromRate = getRate(from) // 相对 HKD
    const toRate = getRate(to) // 相对 HKD
    if (!fromRate || !toRate) return null

    // amount * fromRate / toRate
    return new Decimal(value).mul(fromRate).div(toRate).toNumber()
  }

  const formatConvertedCurrency = (
    amount: number | string,
    fromCcy: string,
    toCcy: string,
    digits = 2
  ): string => {
    const converted = convertCurrencyAmount(amount, fromCcy, toCcy)
    if (converted == null) return '--'
    return numFormat(converted, digits) || '--'
  }
  // #endregion

  const visibleStockSubTabs = computed<SubTabItem[]>(() => {
    const rows = positionData.value?.info?.rows || []
    return STOCK_SUB_TABS.filter((tab) => {
      if (STOCK_TABS_ALWAYS_SHOW.includes(tab.name)) return true
      return hasStockPositionInSubTab(tab.name, rows)
    })
  })

  // ---- cards 计算 ----

  // 其他股票
  const otherStockMarketList = computed(() => {
    const rows = positionData.value?.info?.rows || []
    return rows.filter((item: any) => !knownStockMarketTypes.includes(item.exchangeType))
  })

  const stockCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const fundInfo = fundInfoData.value?.info?.find(
      (item: any) => item.exchangeType.toLowerCase() === sub.toLowerCase()
    ) || {}

    const onlyShowMarketValueTypes = ['TW', 'JP', 'SG', 'other']
    if (onlyShowMarketValueTypes.includes(sub)) {
      if (sub === 'other') {
        const value = otherStockMarketList.value.reduce((acc: number, item: any) => acc + item.marketValueHkd, 0)
        return [{
          label: MARKET_LABEL_MAP[sub] + '(' + activeOtherCurrency.value + ')',
          value: formatConvertedCurrency(value, 'HKD', activeOtherCurrency.value)
        }]
      }
      const list = positionData.value.info.rows?.filter((item: any) => item.exchangeType === sub)
      const value = list.reduce((acc: number, item: any) => acc + item.marketValue, 0)
      return [{
          label: MARKET_LABEL_MAP[sub] || '总市值',
          value: numFormat(value, 2) || '--'
        }]
    }
    return [
      {
        label: MARKET_LABEL_MAP[sub] || '总市值',
        value: numFormat(fundInfo.marketValue, 2) || '--'
      },
      {
        label: '最大购买力',
        value: numFormat(fundInfo.enableBalance, 2) || '--',
        showTip: true,
        tipContent: '「最大购买力」仅供参考，该数据或未能反映帐户的实时最大购买力，以及或未包含其他影响最大购买力的收入和费用等。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「最大购买力」，请电邮至cs@goldhorse.com.hk。'
      },
      {
        label: '冻结资金',
        value: numFormat(fundInfo.frozenBalance, 2) || '--'
      }
    ]
  })

  const cashCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const rows = fundInfoData.value?.info || []

    if (sub === 'OTHER_HKD') {
      const otherFetchBalance = rows.reduce((acc: Decimal, item: any) => {
        if (cashBaseMoneyTypes.includes(item.moneyType)) return acc
        const value = convertCurrencyAmount(item.fetchBalance, item.moneyType, activeOtherCurrency.value)
        if (value == null) return acc
        return acc.plus(value)
      }, new Decimal(0))

      return [
        {
          label: `现金可提(折算 ${activeOtherCurrency.value})`,
          value: numFormat(otherFetchBalance.toNumber(), 2) || '--',
          showTip: true,
          tipContent: '「现金可提」仅供参考，该数据或未能反映账户的实时现金可提的金额，以及或未包含其他影响现金可提的收入和费用，如交易费用和处理费用等。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「现金可提」，请电邮至cs@goldhorse.com.hk。'
        }
      ]
    }

    const fundInfo = fundInfoData.value?.info?.find(
      (item: any) => item.exchangeType.toLowerCase() === sub.toLowerCase()
    ) || {}

    return [
      {
        label: '现金可提',
        value: numFormat(fundInfo.fetchBalance, 2) || '--',
        showTip: true,
        tipContent: '「现金可提」仅供参考，该数据或未能反映账户的实时现金可提的金额，以及或未包含其他影响现金可提的收入和费用，如交易费用和处理费用等。阁下任何依赖或就该数据所作的任何决定，金马证券概不负责。若需查询实际「现金可提」，请电邮至cs@goldhorse.com.hk。'
      }
    ]
  })

  const getExtItem = (type: string, moneyType?: string) => {
    const list: any[] = positionData.value?.extPosInfo || []
    return list.find((item: any) =>
      item.extType.toLowerCase() === type.toLowerCase() && (!moneyType || item.extMoneyType === moneyType)
    ) || {}
  }

  const fundCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const isHKD = sub === 'HKD'
    const extItem = getExtItem('fund', sub)

    return [
      {
        label: isHKD ? '资产净值(港元)' : '资产净值(美元)',
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const bondCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const isHKD = sub === 'bondHKD'
    const moneyType = isHKD ? 'HKD' : 'USD'
    const extItem = getExtItem('bond', moneyType)

    return [
      {
        label: isHKD ? '资产净值(港元)' : '资产净值(美元)',
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const spsCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const extMoneyType = resolveExtMoneyType(sub)
    const extItem = getExtItem('sps', extMoneyType)

    if (isOtherCurrencyExtTab(sub)) {
      return [{
        label: `持仓净值(折算 ${activeOtherCurrency.value})`,
        value: formatConvertedCurrency(extItem.extMarketValue, 'HKD', activeOtherCurrency.value)
      }]
    }

    const label = extMoneyType === 'HKD' ? '持仓净值(港元)' : '持仓净值(美元)'
    return [
      {
        label,
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const extraCards = computed<CardItem[]>(() => {
    const sub = activeSubTab.value
    const extMoneyType = resolveExtMoneyType(sub)
    const extItem = getExtItem('extra', extMoneyType)

    if (isOtherCurrencyExtTab(sub)) {
      return [
        {
          label: `持仓净值(折算 ${activeOtherCurrency.value})`,
          value: formatConvertedCurrency(extItem.extMarketValue, 'HKD', activeOtherCurrency.value)
        }
      ]
    }
    const label = extMoneyType === 'HKD' ? '持仓净值(港元)' : '持仓净值(美元)'
    return [
      {
        label,
        value: numFormat(extItem.extMarketValue, 2) || '--'
      }
    ]
  })

  const currentCards = computed<CardItem[]>(() => {
    switch (activeTopTab.value) {
      case 'stock':
        return stockCards.value
      case 'fund':
        return fundCards.value
      case 'bond':
        return bondCards.value
      case 'sps':
        return spsCards.value
      case 'extra':
        return extraCards.value
      case 'cash':
        return cashCards.value
      default:
        return []
    }
  })

  // ---- columns 计算 ----

  const currentColumns = computed<TableColumn[]>(() => {
    if (activeTopTab.value === 'stock') {
      const sub = activeSubTab.value
      let columns = [...STOCK_COLUMNS]

      if (STOCK_HIDE_AVAILABLE_SUB_TABS.includes(sub)) {
        columns = columns.filter((col) => col.prop !== 'available')
      }

      if (!STOCK_SHOW_CLOSE_PRICE_SUB_TABS.includes(sub)) {
        columns = columns.filter((col) => col.prop !== 'closePrice')
      }

      if (!STOCK_SHOW_CURRENT_PRICE_DISPLAY_SUB_TABS.includes(sub)) {
        columns = columns.filter((col) => col.prop !== 'currentPrice')
      }

      if (!STOCK_SHOW_PROFIT_SUB_TABS.includes(sub)) {
        columns = columns.filter((col) => col.prop !== 'profit')
      }

      if (!STOCK_SHOW_ACTION_SUB_TABS.includes(sub)) {
        columns = columns.filter((col) => col.type !== 'action')
      }

      if (sub === 'other') {
        columns = columns.map((col) => {
          if (col.prop === 'marketValue') {
            return { ...col, label: `总市值(${activeOtherCurrency.value})` }
          }
          if (col.prop === 'cost') {
            return { ...col, label: `成本(${activeOtherCurrency.value})` }
          }
          return col
        })
      }

      return columns
    }

    if (activeTopTab.value === 'sps') {
      const sub = activeSubTab.value
      let columns = [...SPS_COLUMNS]
      const investmentCurrency = isOtherCurrencyExtTab(sub) ? activeOtherCurrency.value : sub

      columns = columns.map((col) => {
        if (col.prop === 'investmentAmount') {
          return { ...col, label: `投资金额（${investmentCurrency}）` }
        }
        return col
      })

      if (isOtherCurrencyExtTab(sub)) {
        columns.splice(1, 0, { prop: 'investmentAmountOriginal', label: '投资金额原币种', minWidth: '180' })
      }

      return columns
    }

    if (activeTopTab.value === 'extra') {
      const sub = activeSubTab.value
      let columns = [...EXTRA_COLUMNS]

      if (isOtherCurrencyExtTab(sub)) {
        columns = columns.map((col) => {
          if (col.prop === 'marketValueDisplay') {
            return { ...col, label: `总市值(${activeOtherCurrency.value})` }
          }
          return col
        })
      }

      return columns
    }

    return COLUMNS_MAP[activeTopTab.value] || []
  })

  // ---- table 计算 ----

  const currentTableData = computed<any[]>(() => {
    const top = activeTopTab.value
    const sub = activeSubTab.value
    const pd = positionData.value

    if (top === 'cash') return []

    let rows: any[] = []
    if (top === 'stock') {
      rows = pd?.info?.rows || []
    } else {
      const extType = EXT_TYPE_MAP[top]
      let moneyType = sub || 'HKD'
      if (top === 'bond') {
        moneyType = sub === 'bondHKD' ? 'HKD' : 'USD'
      } else if (!sub) {
        moneyType = 'HKD'
      }
      const extItem = pd?.extPosInfo?.find((item: any) =>
        item.extType?.toLowerCase() === extType?.toLowerCase() &&
        item.extMoneyType === moneyType
      )
      rows = extItem?.extPositionInfo?.rows || []
    }

    if (top === 'stock') {
      let filtered = []
      if (sub === 'other') {
        filtered = rows.filter((item: any) => !knownStockMarketTypes.includes(item.exchangeType))
      } else {
        filtered = rows.filter((item: any) => {
          if (sub === 'A') {
            return aShareExchangeTypes.includes(item.exchangeType)
          }
          return item.exchangeType === sub
        })
      }

      return filtered.map((item: any) => ({
        code: item.stockCode,
        name: item.stockNameGb || item.stockName,
        marketValue: sub === 'other'
          ? formatConvertedCurrency(item.marketValue, item.moneyType, activeOtherCurrency.value)
          : numFormat(item.marketValue, 2),
        available: item.enableAmount?.toLocaleString() ?? '--',
        hold: item.currentAmount?.toLocaleString() ?? '--',
        cost: sub === 'other'
          ? formatConvertedCurrency(item.costPrice, item.moneyType, activeOtherCurrency.value)
          : numFormat(item.costPrice, 3),
        closePrice: item.closePrice,
        currentPrice: item.currentPriceDisplay ?? '--',
        profit: item.incomeBalance != null ? numFormat(item.incomeBalance, 2) : '--',
        profitPercent: item.incomePercentDisplay ?? '--',
        exchangeType: item.exchangeType,
        fundAccount: item.fundAccount,
        stockCode: item.stockCode,
        stockNameGb: item.stockNameGb
      }))
    }

    if (top === 'fund') {
      const filtered = rows.filter((item: any) => item.moneyType === sub)
      return filtered.map((item: any) => ({
        name: item.stockCode,
        marketValue: item.marketValueDisplay || '--',
        currentAmount: item.currentAmountDisplay || '--',
        productName: item.stockName || '--',
        productCode: item.stockCode || '--'
      }))
    }

    if (top === 'bond') {
      const bondMoneyType = sub === 'bondHKD' ? 'HKD' : 'USD'
      const filtered = rows.filter((item: any) => item.moneyType === bondMoneyType)
      return filtered.map((item: any) => ({
        stockCode: item.stockCode,
        underlying: item.underlying,
        marketValueDisplay: item.marketValueDisplay || '--',
        currentAmount: item.currentAmount || '--',
        maturityDays: item.maturityDays || '--',
        yieldToMaturity: item.yieldToMaturity || '--',
        maturityDate: item.maturityDate || '--'
      }))
    }

    if (top === 'sps') {
      const lifecycles: any[] = lifecycleData.value?.lifecycles || []
      const filtered = rows.filter((item: any) => {
        if (item.subType === 'FCN') {
          return lifecycles.some((lc: any) => lc.isin === item.stockCode)
        }
        return true
      })
      return filtered.map((item: any) => {
        let lifecycle: any = {}
        if (item.subType === 'FCN') {
          lifecycle = lifecycles.find((lc: any) => lc.isin === item.stockCode) || {}
        }
        return {
          ...item,
          subType: lifecycle?.productType || item.subType || '--',
          stockCode: lifecycle?.isin || item.stockCode || '--',
          investmentAmount: isOtherCurrencyExtTab(sub) ? formatConvertedCurrency(item.investmentAmount, item.moneyType, activeOtherCurrency.value) : item.investmentAmount || '--',
          investmentAmountOriginal: isOtherCurrencyExtTab(sub) ? `${numFormat(item.investmentAmount, 2)} ${item.moneyType}` : '--',
          couponRatio: lifecycle.couponRatio ? formatPercent(lifecycle.couponRatio) : item.yieldToMaturity || '--',
          underlying: lifecycle.underlyingPerformance?.map((item: { code: string }) => item.code).join('+') || item.underlying || '--'
        }
      })
    }

    if (top === 'extra') {
      return rows.map((item: any) => ({
        stockName: item.stockName,
        stockCode: item.stockCode,
        marketValueDisplay: isOtherCurrencyExtTab(sub) ? formatConvertedCurrency(item.marketValue, item.moneyType, activeOtherCurrency.value) : item.marketValueDisplay || '--',
        currentAmount: item.currentAmountDisplay || '--'
      }))
    }

    return []
  })

  const summaryOverviewData = computed<SummaryOverviewData>(() => {
    return {
      HKD: {
        totalAsset: formatConvertedCurrency(fundInfoData.value?.asset, fundInfoData.value?.moneyType, 'HKD'),
        totalBuyingPower: formatConvertedCurrency(fundInfoData.value?.enableBalance, fundInfoData.value?.moneyType, 'HKD'),
        totalCash: formatConvertedCurrency(fundInfoData.value?.fetchBalance, fundInfoData.value?.moneyType, 'HKD')
      },
      USD: {
        totalAsset: formatConvertedCurrency(fundInfoData.value?.asset, fundInfoData.value?.moneyType, 'USD'),
        totalBuyingPower: formatConvertedCurrency(fundInfoData.value?.enableBalance, fundInfoData.value?.moneyType, 'USD'),
        totalCash: formatConvertedCurrency(fundInfoData.value?.fetchBalance, fundInfoData.value?.moneyType, 'USD')
      }
    }
  })

  const cashBreakdownData = computed<CashBreakdownData>(() => {
    const rows = fundInfoData.value?.info || []

    const buildBreakdownByCurrency = (targetCurrency: 'HKD' | 'USD'): CashBreakdownItem[] => {
      let hkdFetchBalance = new Decimal(0)
      let usdFetchBalance = new Decimal(0)
      let aShareFetchBalance = new Decimal(0)
      let otherFetchBalance = new Decimal(0)

      rows.forEach((item: any) => {
        if (item.moneyType === 'HKD') {
          hkdFetchBalance = hkdFetchBalance.plus(item.fetchBalance)
          return
        }
        if (item.moneyType === 'USD') {
          usdFetchBalance = usdFetchBalance.plus(item.fetchBalance)
          return
        }
        if (item.moneyType === 'CNY') {
          aShareFetchBalance = aShareFetchBalance.plus(item.fetchBalance)
          return
        }
        const value = convertCurrencyAmount(item.fetchBalance, item.moneyType, targetCurrency)
        if (value == null) return
        otherFetchBalance = otherFetchBalance.plus(value)
      })

      return [
        { label: '港币', value: numFormat(hkdFetchBalance.toNumber(), 2) || '--' },
        { label: '美元', value: numFormat(usdFetchBalance.toNumber(), 2) || '--' },
        { label: '人民币', value: numFormat(aShareFetchBalance.toNumber(), 2) || '--' },
        { label: `其他币种(${targetCurrency})`, value: numFormat(otherFetchBalance.toNumber(), 2) || '--' }
      ]
    }

    return {
      HKD: buildBreakdownByCurrency('HKD'),
      USD: buildBreakdownByCurrency('USD')
    }
  })

  // ---- 操作方法 ----

  const refresh = () => {
    loading.value = true
    refetch({ filter: {} })
  }

  return {
    loading,
    currentCards,
    currentColumns,
    currentTableData,
    visibleStockSubTabs,
    summaryOverviewData,
    cashBreakdownData,
    refreshTime,
    refresh
  }
}
